import React, { useState, useEffect } from 'react';
import { Select, Spin, Form, message } from 'antd';
import { apiMethods, Location, Country } from '../services/api';

interface LocationSelectorProps {
  onLocationSelected?: (country: string, city: string, locationId: string, wikiDataId: string) => void;
}

export const LocationSelector: React.FC<LocationSelectorProps> = ({ onLocationSelected }) => {
  const [locations, setLocations] = useState<Location[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [countries, setCountries] = useState<Country[]>([]);

  useEffect(() => {
    loadLocations();
    loadCountries();
  }, []);

  const loadCountries = async () => {
    try {
      const data = await apiMethods.getCountries();
      setCountries(data);
    } catch (error) {
      console.error('Error loading countries:', error);
    }
  };

  const loadLocations = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await apiMethods.getAllLocations();
      
      if (!data || !Array.isArray(data)) {
        throw new Error('Invalid response format');
      }

      setLocations(data);
    } catch (error: any) {
      console.error('Error loading locations:', error);
      let errorMessage = 'Failed to load locations';
      
      if (error.response?.status === 500) {
        errorMessage = 'Server error: Database connection failed. Please try again later.';
      } else if (error.response?.data?.detail) {
        errorMessage = `Error: ${error.response.data.detail}`;
      } else if (error.message === 'Network Error') {
        errorMessage = 'Network error: Unable to connect to the server';
      }

      setError(errorMessage);
      message.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleLocationChange = async (locationId: string) => {
    const location = locations.find(loc => loc._id === locationId);
    if (!location) {
      message.error('Location not found');
      return;
    }
    
    try {
      setLoading(true);
      
      // Find the country code for the selected location
      const countryMatch = countries.find(c => 
        c.name.common.toLowerCase() === location.country.toLowerCase()
      );

      if (!countryMatch) {
        message.error('Could not find country code. Please try another location.');
        return;
      }

      // Search for the city in GeoDB using the country code
      const cities = await apiMethods.getCities(countryMatch.cca2, location.city);
      const cityMatch = cities.find(city => 
        city.name.toLowerCase() === location.city.toLowerCase()
      );

      if (!cityMatch?.wikiDataId) {
        message.error('Could not find city details. Please try another location.');
        return;
      }

      if (onLocationSelected) {
        onLocationSelected(location.country, location.city, location._id, cityMatch.wikiDataId);
      }
    } catch (error) {
      console.error('Error fetching city details:', error);
      message.error('Failed to get city details');
    } finally {
      setLoading(false);
    }
  };

  const handleRetry = () => {
    loadLocations();
  };

  return (
    <Form.Item
      label="Select Location"
      required
      help={error ? <a onClick={handleRetry}>Click here to retry loading locations</a> : undefined}
      validateStatus={error ? "error" : undefined}
    >
      <Select
        showSearch
        placeholder={loading ? "Loading locations..." : "Type to search locations"}
        loading={loading}
        onChange={handleLocationChange}
        style={{ width: '100%' }}
        filterOption={(input, option) => {
          if (!option) return false;
          const locationText = `${option.label}`.toLowerCase();
          return locationText.includes(input.toLowerCase());
        }}
        options={locations.map(location => ({
          value: location._id,
          label: `${location.country} - ${location.city}`
        }))}
        notFoundContent={loading ? <Spin size="small" /> : error ? "Failed to load locations" : "No locations found"}
      />
    </Form.Item>
  );
}; 